import { computed, effect, inject, Injectable, PLATFORM_ID, signal, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable } from 'rxjs';

import { AuthService, WindowRef } from '@spartacus/core';
import { OrderEntry } from '@spartacus/cart/base/root';

import { ABANDONED_CART_DISPLAYED_KEY, ABANDONED_CART_TIMER_KEY } from '../constants';
import { ConfigurationService } from './configuration.service';
import { InteractionsService } from './interactions.service';

@Injectable({
  providedIn: 'root'
})
export class AbandonedCartTimerService {
  private readonly interactionsService = inject(InteractionsService);
  private readonly configService = inject(ConfigurationService);
  private readonly authService = inject(AuthService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly winRef = inject(WindowRef);

  private displayPopin$ = new BehaviorSubject<boolean>(false);
  private alreadyOpened = signal(false);
  private initialized = signal(false);

  private sessionTimerDelay: number;
  private sessionTimer: any;

  private idleTimerDelay: number;
  private idleTimer: any;

  private cartEntries: Signal<OrderEntry[]>;
  private isLogged: Signal<boolean>;
  private userEvent: Signal<Event>;

  static readonly DEFAULT_TIMER_DELAY = 50;
  static readonly DEFAULT_SESSION_TIMER = 30;
  static readonly DEFAULT_SESSION_TIMER_DELAY = 10;

  private canStartIdleTimer = computed(() => (
    !this.isLogged() && !!this.cartEntries()?.length && !this.alreadyOpened()
  ));

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      effect(() => {
        if (this.initialized()) {
          if (this.canStartIdleTimer()) {
            this.startIdleTimer(this.idleTimerDelay);
          }
        }
      });

      effect(() => {
        if (this.initialized() && this.userEvent() && this.canStartIdleTimer()) {
          this.startIdleTimer(this.idleTimerDelay);
        }
      });
    }
  }

  init(entries$: Observable<OrderEntry[]>): void {
    if (isPlatformBrowser(this.platformId)) {
      const { idleTimerDelay, sessionTimerDelay } = this.configService.config.abandonedCart ?? {};

      this.alreadyOpened.set(!!this.winRef.sessionStorage?.getItem(ABANDONED_CART_DISPLAYED_KEY));
      this.sessionTimerDelay = sessionTimerDelay ? parseInt(sessionTimerDelay) : AbandonedCartTimerService.DEFAULT_SESSION_TIMER * 60;
      this.idleTimerDelay = idleTimerDelay ? parseInt(idleTimerDelay) : AbandonedCartTimerService.DEFAULT_TIMER_DELAY;

      this.userEvent = toSignal(this.interactionsService.watchUserInteractions());
      this.isLogged = toSignal(this.authService.isUserLoggedIn());
      this.cartEntries = toSignal(entries$);
      this.initialized.set(true);

      this.checkExistingTimer();
    }
  }

  checkExistingTimer(): void {
    const storedEndTime = this.winRef.localStorage?.getItem(ABANDONED_CART_TIMER_KEY);
    if (storedEndTime) {
      const endTime = parseInt(storedEndTime);
      const currentTime = new Date().getTime();

      if (currentTime > endTime) {
        this.startSessionTimer();
      }
    }
  }

  // Timer for triggering abandoned cart popin after staying (default value:) 30 min in same session
  initSessionTimer(): void {
    if (this.initialized()) {
      const startTime = new Date().getTime();
      const endTime = startTime + (this.sessionTimerDelay * 1000);
      this.winRef.localStorage?.setItem(ABANDONED_CART_TIMER_KEY, endTime.toString());
    }
  }

  get isDisplayPopin$(): Observable<boolean> {
    return this.displayPopin$.asObservable();
  }

  private startSessionTimer(): void {
    this.clearSessionTimer();

    this.sessionTimer = setTimeout(() => {
      this.triggerPopinIfNotAlreadyOpened();
      this.clearSessionTimer();
    }, AbandonedCartTimerService.DEFAULT_SESSION_TIMER_DELAY * 1000);
  }

  private clearSessionTimer(): void {
    if (this.sessionTimer) {
      clearTimeout(this.sessionTimer);
      this.sessionTimer = null;
    }
  }

  // Timer for triggering abandoned cart popin when idle
  private startIdleTimer(duration: number) {
    this.clearIdleTimer();

    this.idleTimer = setTimeout(() => {
      this.triggerPopinIfNotAlreadyOpened();
      this.clearIdleTimer();
    }, duration * 1000);
  }

  private clearIdleTimer(): void {
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
  }

  private triggerPopinIfNotAlreadyOpened() {
    if (!this.alreadyOpened()) {
      this.clearTimerState();
      this.triggerPopin();
    }
  }

  private clearTimerState(): void {
    this.winRef.localStorage?.removeItem(ABANDONED_CART_TIMER_KEY);
  }

  private triggerPopin(): void {
    this.winRef.sessionStorage?.setItem(ABANDONED_CART_DISPLAYED_KEY, JSON.stringify(true));
    this.displayPopin$.next(true);
    this.alreadyOpened.set(true);
  }
}
